import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { PieChart } from "@mui/x-charts/PieChart";

interface Props {
  totals: { empty_families: number; missing_things: number };
  stepSummary: { step: string; empty_families: number; missing_things: number }[];
}

const STEP_COLORS: { [key: string]: string } = {
  fetcher: "#607D8B",
  unifier: "#E91E63",
  advisor: "#2196F3",
};

const CARD_DEFS = [
  {
    key: "empty_families" as const,
    title: "EMPTY FAMILIES",
    description: "Families with zero brands.",
    perStepDescription: "Families with zero brands, per step.",
    alwaysDonut: true,
  },
  {
    key: "missing_things" as const,
    title: "FAMILIES MISSING MAPS/CONFIGS",
    description: "Families that won’t process anything until one is attached.",
    perStepDescription:
      "Families that won’t process anything until one is attached, per step.",
    alwaysDonut: false,
  },
];

function SimpleTotalCard({
  title,
  description,
  count,
}: {
  title: string;
  description: string;
  count: number;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #2E7D3280",
        background: "#EDF7ED",
        borderRadius: "8px",
        padding: "8px",
        height: "66px",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            pb={0.5}
            sx={{ fontSize: "16px", color: "#000000DE", fontWeight: 500 }}
          >
            {title}
          </Typography>
          <Typography pb={0.5} sx={{ fontSize: "14px", color: "#00000099" }}>
            {description}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#2E7D32",
            lineHeight: 1,
          }}
        >
          {count}
        </Typography>
      </Box>
    </Box>
  );
}

function DonutBreakdownCard({
  title,
  description,
  total,
  legend,
  data,
}: {
  title: string;
  description: string;
  total: number;
  legend: { label: string; color: string; count: number }[];
  data: { id: number; value: number; color: string }[];
}) {
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #0000001F",
        borderRadius: "8px",
        padding: "8px",
        height: "96px",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            pb={0.5}
            sx={{ fontSize: "16px", color: "#000000DE", fontWeight: 500 }}
          >
            {title}
          </Typography>
          <Typography pb={0.5} sx={{ fontSize: "14px", color: "#00000099" }}>
            {description}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {legend.map((item) => (
              <Box
                key={item.label}
                sx={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: item.color,
                    borderRadius: "2px",
                  }}
                />
                <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                  {item.label}
                  <span style={{ fontWeight: 700 }}> {item.count}</span>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ width: "80px" }}>
          <PieChart
            series={[
              {
                data,
                innerRadius: 30,
                outerRadius: 40,
                paddingAngle: 0,
                cornerRadius: 0,
              },
            ]}
            width={80}
            height={80}
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            slotProps={{ legend: { hidden: true } }}
          >
            {/* center label */}
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: 16,
                fontWeight: 700,
                fill: "#000000DE",
              }}
            >
              {total}
            </text>
          </PieChart>
        </Box>
      </Box>
    </Box>
  );
}

const NeedAttentionHeader = ({ totals, stepSummary }: Props) => {
  return (
    <Box sx={{ padding: "10px 16px 20px" }}>
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          width: "100%",
        }}
      >
        {CARD_DEFS.map((cardDef) => {
          const total = totals[cardDef.key] ?? 0;

          if (total === 0 && !cardDef.alwaysDonut) {
            return (
              <SimpleTotalCard
                key={cardDef.key}
                title={cardDef.title}
                description={cardDef.description}
                count={total}
              />
            );
          }

          const legend = stepSummary.map((step) => ({
            label: step.step.charAt(0).toUpperCase() + step.step.slice(1),
            color: STEP_COLORS[step.step] ?? "#9e9e9e",
            count: step[cardDef.key] ?? 0,
          }));

          // an all-zero pie has no arcs to draw — fall back to a plain grey ring
          const data =
            total === 0
              ? [{ id: 0, value: 1, color: "#E0E0E0" }]
              : stepSummary.map((step, index) => ({
                  id: index,
                  value: step[cardDef.key] ?? 0,
                  color: STEP_COLORS[step.step] ?? "#9e9e9e",
                }));

          return (
            <DonutBreakdownCard
              key={cardDef.key}
              title={cardDef.title}
              description={cardDef.perStepDescription}
              total={total}
              legend={legend}
              data={data}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default NeedAttentionHeader;
